/**
 * NewStoryModal — focused story-creation flow. Deliberately light: a title,
 * an optional description/genre/goal, and a fast escape hatch to skip
 * straight into the editor.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Feather } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useMyWriting } from './context/MyWritingContext';
import { GENRES } from './constants';

function ModalBody({ onClose }) {
  const navigate = useNavigate();
  const { createStory } = useMyWriting();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('Fiction');
  const [goal, setGoal] = useState('');

  const start = async (blank = false) => {
    try {
      const story = await createStory({
        title: blank ? '' : title,
        description: blank ? '' : description,
        genre: blank ? 'Fiction' : genre,
        goal: !blank && goal ? Number(goal) : null,
      });
      onClose();
      navigate(`/app/studio/${story.id}`);
    } catch (error) {
      toast(error.message || 'We could not create your manuscript.');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-coral/12 text-coral"><Feather className="h-4 w-4" /></span>
        <h2 className="font-display text-xl font-semibold text-ink">Start a new manuscript</h2>
      </div>

      <div className="mt-5">
        <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Manuscript title</label>
        <Input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Give your manuscript a name…"
          data-testid="my-writing-new-title"
          className="mt-1.5 rounded-xl border-line bg-cream font-display text-base"
        />
      </div>

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Short description</label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What's it about?"
          rows={3}
          className="mt-1.5 rounded-xl border-line bg-cream text-sm"
        />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Genre</label>
          <Select value={genre} onValueChange={setGenre}>
            <SelectTrigger className="mt-1.5 rounded-xl border-line bg-cream"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Writing goal <span className="normal-case text-subtle/70">(optional)</span></label>
          <Input
            type="number"
            min="0"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. 50000"
            className="mt-1.5 rounded-xl border-line bg-cream"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-col-reverse items-stretch gap-2.5 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={() => start(true)}
          data-testid="my-writing-blank-story"
          className="text-xs font-medium text-subtle underline-offset-2 hover:text-coral hover:underline"
        >
          Start with a blank manuscript
        </button>
        <Button
          variant="primary"
          icon={false}
          onClick={() => start(false)}
          disabled={!title.trim()}
          data-testid="my-writing-start-writing"
        >
          Start Writing
        </Button>
      </div>
    </div>
  );
}

export default function NewStoryModal({ isOpen, onClose }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="w-[92vw] max-w-lg rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">Start a new manuscript</DialogTitle>
          <ModalBody onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">Start a new manuscript</DrawerTitle>
        <ModalBody onClose={onClose} />
      </DrawerContent>
    </Drawer>
  );
}
